/* eslint-disable consistent-return */
/* eslint-disable no-plusplus */
/* eslint-disable no-continue */
import React, { useEffect, useRef, useState } from 'react'
import * as Styles from './glitterEffect.module.scss'

// Breakpoint for md screens (matches breakpoints.scss)
const MD_BREAKPOINT = 768

// Particle count per screen size
const PARTICLE_COUNT = {
  mobile: 30000,
  desktop: 110000,
}

export type GlitterColorScheme = 'pink' | 'red' | 'white'

type Props = {
  colorScheme?: GlitterColorScheme
}

const GlitterEffect = ({ colorScheme = 'pink' }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isVisible, setIsVisible] = useState(true)
  const [isReady, setIsReady] = useState(false)

  // Defer initialization to not block initial page load
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 50)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!isReady) return

    const canvas = canvasRef.current
    if (!canvas) return

    // Check if OffscreenCanvas is supported
    if (typeof canvas.transferControlToOffscreen !== 'function') {
      // OffscreenCanvas not supported, skip effect
      return
    }

    // Set canvas size before transferring
    const width = window.innerWidth
    const height = window.innerHeight
    canvas.width = width
    canvas.height = height

    // Determine particle count based on screen width
    const isDesktop = width >= MD_BREAKPOINT
    const particleCount = isDesktop
      ? PARTICLE_COUNT.desktop
      : PARTICLE_COUNT.mobile

    // Transfer canvas control to offscreen
    const offscreen = canvas.transferControlToOffscreen()

    // Create worker
    const worker = new Worker(
      // @ts-ignore - Webpack 5 handles this import.meta.url pattern at build time
      new URL('./glitterEffect.worker.ts', import.meta.url)
    )

    // Listen for completion
    worker.onmessage = (e: MessageEvent) => {
      if (e.data.type === 'complete') {
        setIsVisible(false)
        worker.terminate()
      }
    }

    // Send canvas to worker (transfers ownership)
    worker.postMessage(
      {
        type: 'init',
        canvas: offscreen,
        width,
        height,
        particleCount,
        colorScheme,
      },
      [offscreen]
    )

    return () => {
      worker.postMessage({ type: 'stop' })
      worker.terminate()
    }
  }, [colorScheme, isReady])

  if (!isVisible) return null

  return (
    <canvas
      ref={canvasRef}
      className={Styles.glitterContainer}
      aria-hidden="true"
    />
  )
}

export default GlitterEffect
